// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { chargeDateKey } from '@/project/utilities/charge-date';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { Decimal } from 'decimal.js';

export interface AmountRange {
  min: Decimal;
  max: Decimal;
}

/** Parses a charge's total price; an unparseable amount is zero. */
export function parseChargeTotal(charge: ChargeFormValue): Decimal {
  return (
    parseCurrencyAmount(charge.totalPrice.amount) ?? new CurrencyDecimal(0)
  );
}

/** Sums the total prices of a set of charges. */
export function sumChargeTotals(charges: ChargeFormValue[]): Decimal {
  return charges.reduce(
    (sum, charge) => sum.plus(parseChargeTotal(charge)),
    new CurrencyDecimal(0)
  );
}

function toAmountRange(amounts: Decimal[]): AmountRange | null {
  if (amounts.length === 0) {
    return null;
  }

  return {
    min: CurrencyDecimal.min(...amounts),
    max: CurrencyDecimal.max(...amounts),
  };
}

/** Finds the smallest and largest charge amount in a schedule. */
export function findChargeAmountRange(
  charges: ChargeFormValue[]
): AmountRange | null {
  return toAmountRange(charges.map(parseChargeTotal));
}

/** Finds the smallest and largest invoice amount in a schedule. */
export function findInvoiceAmountRange(
  charges: ChargeFormValue[]
): AmountRange | null {
  // Charges that share a bill date land on the same invoice, so each
  // invoice's amount is the sum of its dated charges.
  const invoiceTotals = new Map<string, Decimal>();
  for (const charge of charges) {
    const invoiceDate = chargeDateKey(charge.expectedBillableDate);
    if (invoiceDate === null) {
      continue;
    }

    const invoiceTotal =
      invoiceTotals.get(invoiceDate) ?? new CurrencyDecimal(0);
    invoiceTotals.set(invoiceDate, invoiceTotal.plus(parseChargeTotal(charge)));
  }

  return toAmountRange(Array.from(invoiceTotals.values()));
}

/** Formats a range as "$33.33–$33.34", or one amount when both ends match. */
export function formatAmountRange(
  range: AmountRange,
  currencyCode: IsoCurrencyCode
): string {
  const min = formatCurrencyValue({ amount: range.min, isoCode: currencyCode });
  if (range.min.equals(range.max)) {
    return min;
  }

  return `${min}–${formatCurrencyValue({ amount: range.max, isoCode: currencyCode })}`;
}
